import { useMemo, useState } from "react";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGetCurrentLocalFacilityId } from "modules/facility/facilityHooks";
import type { ClubhouseAdKitImageDto } from "entities/responses/adkitImage";
import { useGetFacilityImagesQuery } from "../../api/endpoints/images";
import type { PlacementCatalogEntry } from "../../placementCatalog";
import { ImagePreview } from "../library/ImageCard";
import {
  formatImageDimensions,
  imageDisplayName,
} from "../library/imageLibraryViewModel";
import { ConfirmActionDialog } from "../detail/ConfirmActionDialog";
import { HoleSponsors } from "./HoleSponsors";
import { ImagePickerDialog } from "./ImagePickerDialog";
import { SetupSection } from "./ImagesStep";
import { StartOption } from "./NameStep";
import {
  addImage,
  imageModeSwitchDropsData,
  moveImage,
  removeImage,
  setImageMode,
  type PlacementSetup,
} from "./campaignDraft";

interface ImagesQuestionProps {
  number: number;
  placement: PlacementCatalogEntry;
  setup: PlacementSetup;
  onChange: (setup: PlacementSetup) => void;
}

/** Library images by id, so a draft's image ids can show names and sizes. */
export function useLibraryImagesById() {
  const facilityId = useGetCurrentLocalFacilityId();
  const { data = [] } = useGetFacilityImagesQuery(
    { facilityId, includeDeletedImages: true },
    { skip: !facilityId },
  );
  return useMemo(
    () => new Map(data.map((image) => [String(image.id), image])),
    [data],
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      size="icon"
      variant="outline"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="size-8 rounded-lg"
    >
      {children}
    </Button>
  );
}

/**
 * Question 2. "Same images on every hole" is an ordered list; placements
 * whose metadata.canAssignToSpecificHoles is true can switch to hole sponsors.
 */
export function ImagesQuestion({
  number,
  placement,
  setup,
  onChange,
}: ImagesQuestionProps) {
  const imagesById = useLibraryImagesById();
  const [pendingMode, setPendingMode] = useState<
    PlacementSetup["imageMode"] | null
  >(null);
  const chooseMode = (mode: PlacementSetup["imageMode"]) => {
    if (mode === setup.imageMode) return;
    if (imageModeSwitchDropsData(setup)) setPendingMode(mode);
    else onChange(setImageMode(setup, mode));
  };

  return (
    <SetupSection
      number={number}
      title={
        placement.canAssignToSpecificHoles
          ? "What shows on those holes?"
          : "Which images?"
      }
    >
      {placement.canAssignToSpecificHoles && (
        <div
          role="radiogroup"
          aria-label="What shows on those holes"
          className="flex flex-wrap gap-3"
        >
          <StartOption
            selected={setup.imageMode === "same"}
            onSelect={() => chooseMode("same")}
            heading="Same images on every hole"
          >
            Your images take turns. {placement.howItChanges}.
          </StartOption>
          <StartOption
            selected={setup.imageMode === "sponsors"}
            onSelect={() => chooseMode("sponsors")}
            heading="Hole sponsors"
          >
            A sponsor owns a hole: its image shows on the holes you give it
          </StartOption>
        </div>
      )}

      {setup.imageMode === "sponsors" ? (
        <HoleSponsors
          placement={placement}
          setup={setup}
          imagesById={imagesById}
          onChange={onChange}
        />
      ) : (
        <SameImagesList
          placement={placement}
          setup={setup}
          imagesById={imagesById}
          onChange={onChange}
        />
      )}

      <ConfirmActionDialog
        open={pendingMode !== null}
        onOpenChange={(open) => !open && setPendingMode(null)}
        title={
          pendingMode === "sponsors"
            ? "Switch to hole sponsors?"
            : "Switch to the same images on every hole?"
        }
        description={
          pendingMode === "sponsors"
            ? "The images in the list are cleared from this placement. They stay in your library."
            : "The hole sponsors are cleared from this placement. Their images stay in your library."
        }
        confirmLabel="Switch"
        onConfirm={() => {
          if (pendingMode) onChange(setImageMode(setup, pendingMode));
          setPendingMode(null);
        }}
        isLoading={false}
      />
    </SetupSection>
  );
}

function SameImagesList({
  placement,
  setup,
  imagesById,
  onChange,
}: {
  placement: PlacementCatalogEntry;
  setup: PlacementSetup;
  imagesById: Map<string, ClubhouseAdKitImageDto>;
  onChange: (setup: PlacementSetup) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const orderHelp = placement.hasDynamicRotation
    ? "Rotation order: images take turns in this order."
    : "First image shows first.";

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <span className="text-sm text-text-muted">{orderHelp}</span>
        <Button
          size="sm"
          className="rounded-lg text-xs"
          onClick={() => setPickerOpen(true)}
        >
          + Add images
        </Button>
      </div>

      {setup.images.length === 0 ? (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="rounded-xl border-[1.5px] border-dashed border-amber-300 bg-amber-50 px-5 py-7 text-center text-sm text-ink dark:border-amber-800 dark:bg-amber-950/30"
        >
          <strong className="font-semibold">No images yet.</strong> Add at least
          one image to finish this placement.
        </button>
      ) : (
        <ol
          aria-label={`${placement.name} images, in order`}
          className="flex flex-col gap-2"
        >
          {setup.images.map((id, index) => {
            const image: ClubhouseAdKitImageDto | undefined = imagesById.get(
              String(id),
            );
            const name = image ? imageDisplayName(image) : "Image not found";
            return (
              <li
                key={id}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-border px-3 py-2.5"
              >
                <span className="w-5 text-center text-sm font-bold text-ink">
                  {index + 1}
                </span>
                {image && <ImagePreview image={image} className="h-11 w-22" />}
                <div className="min-w-0 flex-[1_1_140px]">
                  <div className="truncate text-sm font-semibold text-ink">
                    {name}
                  </div>
                  {image && (
                    <div className="flex items-center gap-1 text-xs font-semibold text-good">
                      <Check className="size-3" aria-hidden="true" />
                      {formatImageDimensions(image)}
                    </div>
                  )}
                </div>
                <span className="flex gap-1.5">
                  <IconButton
                    label={`Move ${name} up`}
                    disabled={index === 0}
                    onClick={() => onChange(moveImage(setup, index, -1))}
                  >
                    <ChevronUp className="size-3.5" />
                  </IconButton>
                  <IconButton
                    label={`Move ${name} down`}
                    disabled={index === setup.images.length - 1}
                    onClick={() => onChange(moveImage(setup, index, 1))}
                  >
                    <ChevronDown className="size-3.5" />
                  </IconButton>
                  <IconButton
                    label={`Remove ${name} from ${placement.name}`}
                    onClick={() => onChange(removeImage(setup, index))}
                  >
                    <X className="size-3.5 text-destructive" />
                  </IconButton>
                </span>
              </li>
            );
          })}
        </ol>
      )}

      <ImagePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        placement={placement}
        addedIds={setup.images.map(String)}
        onAdd={(image) => onChange(addImage(setup, image.id))}
      />
    </>
  );
}
